import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { LocationProvider } from '@/context/LocationContext';
import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
// Eager: the public conversion path — everything a first-time visitor hits.
import LandingPage from '@/pages/LandingPage';
import LoginPage from '@/pages/LoginPage';
import RegisterPage from '@/pages/RegisterPage';
import { ChatWidget } from '@/components/ChatWidget';
import { CookieConsent } from '@/components/CookieConsent';
import SplashScreen from '@/components/SplashScreen';
import '@/App.css';

// Lazy: every signed-in surface loads as its own chunk on first navigation —
// the landing page must not pay the parse cost of dashboards and admin.
const BrowseStylists = lazy(() => import('@/pages/BrowseStylists'));
const ServiceSelectionPage = lazy(() => import('@/pages/ServiceSelectionPage'));
const ClientDashboard = lazy(() => import('@/pages/ClientDashboard'));
const StylistDashboard = lazy(() => import('@/pages/StylistDashboard'));
const BookingPage = lazy(() => import('@/pages/EnhancedBookingPage'));
const FavoritesPage = lazy(() => import('@/pages/FavoritesPage'));
const MessagingPage = lazy(() => import('@/pages/MessagingPage'));
const StylistEarningsPage = lazy(() => import('@/pages/StylistEarningsPage'));
const StylistProfilePage = lazy(() => import('@/pages/StylistProfilePage'));
const StylistCalendarPage = lazy(() => import('@/pages/StylistCalendarPage'));
const StylistAnalyticsPage = lazy(() => import('@/pages/StylistAnalyticsPage'));
const AdminLayout = lazy(() => import('@/pages/admin/AdminLayout'));
const AdminOverview = lazy(() => import('@/pages/admin/AdminOverview'));
const AdminOnboarding = lazy(() => import('@/pages/admin/AdminOnboarding'));
const AdminCredentials = lazy(() => import('@/pages/admin/AdminCredentials'));
const AdminPhotos = lazy(() => import('@/pages/admin/AdminPhotos'));
const AdminPayslips = lazy(() => import('@/pages/admin/AdminPayslips'));
const AdminCommunications = lazy(() => import('@/pages/admin/AdminCommunications'));
const AdminUsers = lazy(() => import('@/pages/admin/AdminUsers'));
const AdminClients = lazy(() => import('@/pages/admin/AdminClients'));
const AdminStylists = lazy(() => import('@/pages/admin/AdminStylists'));
const AdminPartners = lazy(() => import('@/pages/admin/AdminPartners'));
const AdminAdmins = lazy(() => import('@/pages/admin/AdminAdmins'));
const AdminTickets = lazy(() => import('@/pages/admin/AdminTickets'));
const SettingsPage = lazy(() => import('@/pages/SettingsPage'));
const LoyaltyPage = lazy(() => import('@/pages/LoyaltyPage'));
const PrivacyPolicyPage = lazy(() => import('@/pages/PrivacyPolicyPage'));
const TermsPage = lazy(() => import('@/pages/TermsPage'));
const InvestorsPage = lazy(() => import('@/pages/InvestorsPage'));
const PaymentSuccessPage = lazy(() => import('@/pages/PaymentSuccessPage'));
const PaymentCancelledPage = lazy(() => import('@/pages/PaymentCancelledPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/pages/ResetPasswordPage'));
const AdminSetupPage = lazy(() => import('@/pages/AdminSetupPage'));
const GoogleCallbackPage = lazy(() => import('@/pages/GoogleCallbackPage'));
const OnboardingWizard = lazy(() => import('@/pages/OnboardingWizard'));
const AppointmentRatingPage = lazy(() => import('@/pages/AppointmentRatingPage'));

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
    </div>
  );
}

// Layout wrapper to conditionally show footer
function Layout({ children }) {
  const location = useLocation();
  const { user } = useAuth();
  const noFooterPaths = ['/messages'];
  const showFooter = !noFooterPaths.includes(location.pathname);
  const isLanding = location.pathname === '/';
  
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navigation user={user} />
      <main className={`flex-1 ${isLanding ? '' : 'pt-[72px]'}`}>{children}</main>
      {showFooter && <Footer />}
    </div>
  );
}

// Protected Route component
function ProtectedRoute({ children, allowedRole }) {
  const { user, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (allowedRole && user.role !== allowedRole && user.role !== 'admin') {
    return <Navigate to="/" replace />;
  }
  
  return children;
}

// Admin Route component
function AdminRoute({ children }) {
  const { user, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!user || user.role !== 'admin') {
    return <Navigate to="/" replace />;
  }
  
  return children;
}

function AppRoutes() {
  return (
    <Layout>
      <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/browse" element={<BrowseStylists />} />
        <Route path="/discover" element={<Navigate to="/browse" replace />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/investors" element={<InvestorsPage />} />
        <Route path="/payment-success" element={<PaymentSuccessPage />} />
        <Route path="/payment-cancelled" element={<PaymentCancelledPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/auth/callback" element={<GoogleCallbackPage />} />
        <Route path="/onboarding" element={<ProtectedRoute allowedRole="stylist"><OnboardingWizard /></ProtectedRoute>} />
        <Route path="/admin-setup" element={<AdminSetupPage />} />
        <Route
          path="/rate/:bookingId"
          element={
            <ProtectedRoute allowedRole="client">
              <AppointmentRatingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <SettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/services"
          element={
            <ProtectedRoute allowedRole="client">
              <ServiceSelectionPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/favorites"
          element={
            <ProtectedRoute allowedRole="client">
              <FavoritesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/messages"
          element={
            <ProtectedRoute>
              <MessagingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/dashboard"
          element={
            <ProtectedRoute allowedRole="client">
              <ClientDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/stylist/dashboard"
          element={
            <ProtectedRoute allowedRole="stylist">
              <StylistDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/stylist/earnings"
          element={
            <ProtectedRoute allowedRole="stylist">
              <StylistEarningsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/stylist/profile"
          element={
            <ProtectedRoute allowedRole="stylist">
              <StylistProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/stylist/calendar"
          element={
            <ProtectedRoute allowedRole="stylist">
              <StylistCalendarPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/stylist/analytics"
          element={
            <ProtectedRoute allowedRole="stylist">
              <StylistAnalyticsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/loyalty"
          element={
            <ProtectedRoute>
              <LoyaltyPage />
            </ProtectedRoute>
          }
        />
        <Route path="/splash" element={<SplashScreen duration={999999} />} />
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <AdminLayout />
            </AdminRoute>
          }
        >
          <Route index element={<AdminOverview />} />
          <Route path="onboarding" element={<AdminOnboarding />} />
          <Route path="credentials" element={<AdminCredentials />} />
          <Route path="photos" element={<AdminPhotos />} />
          <Route path="payslips" element={<AdminPayslips />} />
          <Route path="communications" element={<AdminCommunications />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="clients" element={<AdminClients />} />
          <Route path="stylists" element={<AdminStylists />} />
          <Route path="partners" element={<AdminPartners />} />
          <Route path="admins" element={<AdminAdmins />} />
          <Route path="tickets" element={<AdminTickets />} />
        </Route>
        <Route
          path="/booking/:stylistId"
          element={
            <ProtectedRoute>
              <BookingPage />
            </ProtectedRoute>
          }
        />
      </Routes>
      </Suspense>
    </Layout>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <LocationProvider>
          <AppRoutes />
          <ChatWidget />
          <CookieConsent />
          <Toaster position="top-right" richColors theme="dark" />
        </LocationProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
